import { usePlayerStore } from '../stores'
import { Users, TrendingUp, MapPin, Award, Star, Zap, Shield, Target } from 'lucide-react'
import { Player } from '../types'

export default function AdminInsights() {
  const { players } = usePlayerStore()

  // 1. Position Distribution
  const posStats = players.reduce((acc, p) => {
    const pos = p.main_position || 'Unknown'
    acc[pos] = (acc[pos] || 0) + 1
    return acc
  }, {} as Record<string, number>)



  // 3. Performance Rankings
  const getTopPlayers = (field: keyof Player, limit = 5) => {
    return [...players]
      .sort((a, b) => ((b[field] as number) || 0) - ((a[field] as number) || 0))
      .slice(0, limit)
  }

  const topScorers = getTopPlayers('goals')
  const topAssisters = getTopPlayers('assists')
  const topPlaymakers = getTopPlayers('kp') // Key Passes
  const topDribblers = getTopPlayers('dr') // Dribbles
  const topDefenders = getTopPlayers('du') // Defensive Actions


  
  // Stats for regional help
  const stateStats = players.reduce((acc, p) => {
    if (p.state) acc[p.state] = (acc[p.state] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const topStates = Object.entries(stateStats)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)

  return (
    <div className="space-y-12 animate-in fade-in duration-500 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-3xl font-black text-white flex items-center gap-3">
            <TrendingUp size={32} className="text-[#7cff01]" />
            Global Player Insights
          </h2>
          <p className="text-gray-400 text-sm font-medium">Reporting on {players.length} registered players across the platform</p>
        </div>
        <div className="text-[10px] font-black text-[#7cff01] uppercase tracking-[0.2em] bg-[#7cff01]/10 px-4 py-2 rounded-full border border-[#7cff01]/20 backdrop-blur-sm self-start md:self-center">
          LIVE DATABASE FEED • {new Date().toLocaleDateString()}
        </div>
      </div>

      {/* Primary Rankings: Golden Boot & Assist Kings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Golden Boot */}
        <div className="bg-[#021a1a] border border-white/5 rounded-[32px] p-8 space-y-8">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-black text-white flex items-center gap-3">
              <Award size={24} className="text-[#7cff01]" />
              Golden Boot Leaderboard
            </h3>
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Total Goals</span>
          </div>
          <div className="space-y-6">
            {topScorers.map((player, idx) => (
              <div key={player.id} className="flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <span className={`text-2xl font-black ${idx === 0 ? 'text-[#7cff01]' : 'text-gray-700'}`}>
                    0{idx + 1}
                  </span>
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-900 border border-white/5">
                    <img src={player.photo_url || '/placeholder-user.png'} alt={player.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-bold text-white group-hover:text-[#7cff01] transition-colors">{player.name}</p>
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{player.club || 'Independent'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-3xl font-black ${idx === 0 ? 'text-[#7cff01]' : 'text-white'}`}>{player.goals || 0}</p>
                  <p className="text-[10px] text-gray-600 font-bold uppercase">Goals</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Assist Kings */}
        <div className="bg-[#021a1a] border border-white/5 rounded-[32px] p-8 space-y-8">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-black text-white flex items-center gap-3">
               <Star size={24} className="text-blue-400" />
               Assist Kings
            </h3>
             <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Total Assists</span>
          </div>
          <div className="space-y-6">
            {topAssisters.map((player, idx) => (
              <div key={player.id} className="flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <span className={`text-2xl font-black ${idx === 0 ? 'text-blue-400' : 'text-gray-700'}`}>
                    0{idx + 1}
                  </span>
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-900 border border-white/5">
                    <img src={player.photo_url || '/placeholder-user.png'} alt={player.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-bold text-white group-hover:text-blue-400 transition-colors">{player.name}</p>
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{player.club || 'Independent'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-3xl font-black ${idx === 0 ? 'text-blue-400' : 'text-white'}`}>{player.assists || 0}</p>
                  <p className="text-[10px] text-gray-600 font-bold uppercase">Assists</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Specialist Leaderboards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
         {/* Top Playmakers (Key Passes) */}
         <div className="bg-[#0d1117] border border-white/5 rounded-[24px] p-6">
            <h4 className="flex items-center gap-2 text-sm font-black text-white uppercase tracking-widest mb-6 border-b border-white/5 pb-4">
               <Zap size={16} className="text-yellow-400" />
               Highest Vision (KP)
            </h4>
            <div className="space-y-4">
               {topPlaymakers.map(p => (
                  <div key={p.id} className="flex justify-between items-center text-sm">
                     <span className="text-gray-400 font-bold truncate max-w-[120px]">{p.name}</span>
                     <span className="font-black text-yellow-400">{p.kp || 0} <span className="text-[10px] text-gray-600">KP</span></span>
                  </div>
               ))}
            </div>
         </div>

         {/* Top Dribblers */}
         <div className="bg-[#0d1117] border border-white/5 rounded-[24px] p-6">
            <h4 className="flex items-center gap-2 text-sm font-black text-white uppercase tracking-widest mb-6 border-b border-white/5 pb-4">
               <Target size={16} className="text-purple-400" />
               Dynamism (Dribbles)
            </h4>
            <div className="space-y-4">
               {topDribblers.map(p => (
                  <div key={p.id} className="flex justify-between items-center text-sm">
                     <span className="text-gray-400 font-bold truncate max-w-[120px]">{p.name}</span>
                     <span className="font-black text-purple-400">{p.dr || 0} <span className="text-[10px] text-gray-600">DR</span></span>
                  </div>
               ))}
            </div>
         </div>

         {/* Defensive Walls */}
         <div className="bg-[#0d1117] border border-white/5 rounded-[24px] p-6">
            <h4 className="flex items-center gap-2 text-sm font-black text-white uppercase tracking-widest mb-6 border-b border-white/5 pb-4">
               <Shield size={16} className="text-emerald-400" />
               Defensive Walls (Tackles)
            </h4>
            <div className="space-y-4">
               {topDefenders.map(p => (
                  <div key={p.id} className="flex justify-between items-center text-sm">
                     <span className="text-gray-400 font-bold truncate max-w-[120px]">{p.name}</span>
                     <span className="font-black text-emerald-400">{p.du || 0} <span className="text-[10px] text-gray-600">DU</span></span>
                  </div>
               ))}
            </div>
         </div>
      </div>

      {/* Original Distribution Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-8 border-t border-white/5">
        <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-8">
          <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
            <Users size={20} className="text-green-500" />
            Positional Distribution
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(posStats)
              .sort((a, b) => b[1] - a[1])
              .map(([pos, count]) => (
                <div key={pos} className="bg-gray-950/50 p-4 rounded-2xl border border-gray-800 flex justify-between items-center">
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">{pos}</span>
                  <span className="text-lg font-black text-white">{count}</span>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-8">
           <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
             <MapPin size={20} className="text-red-500" />
             Hot Talent State Hubs
           </h3>
           <div className="space-y-3">
              {topStates.map(([state, count], idx) => (
                 <div key={state} className="flex items-center justify-between p-3 bg-gray-950 rounded-xl border border-gray-800">
                    <div className="flex items-center gap-3">
                       <span className="text-xs font-black text-gray-600">{idx + 1}</span>
                       <span className="text-sm font-bold text-white">{state}</span>
                    </div>
                    <span className="text-xs font-bold text-green-500">{count} Producers</span>
                 </div>
              ))}
           </div>
        </div>
      </div>
    </div>
  )
}
